import {
	performGqlFetch,
	type GqlSuccessResponse,
} from '@atlassian/conversation-assistant-utils/graphqlUtils';

type RovoChatAgentResponse = {
	agentStudio_getAgents?: {
		edges?: Array<{
			node?: {
				identityAccountId?: string | null;
			};
		}>;
	};
};

type RovoChatAgentVariables = {
	cloudId: string;
	product: string;
};

const rovoChatAgentIdentityRequests = new Map<
	string,
	Promise<GqlSuccessResponse<RovoChatAgentResponse>>
>();

const getIdentityAccountId = async ({
	cacheKey,
	request,
}: {
	cacheKey: string;
	request: Promise<GqlSuccessResponse<RovoChatAgentResponse>>;
}): Promise<string | undefined> => {
	try {
		const response = await request;
		return response.data.agentStudio_getAgents?.edges?.[0]?.node?.identityAccountId ?? undefined;
	} catch {
		if (rovoChatAgentIdentityRequests.get(cacheKey) === request) {
			rovoChatAgentIdentityRequests.delete(cacheKey);
		}
		return undefined;
	}
};

export const fetchRovoChatAgentIdentityAccountId = async ({
	cloudId,
	product,
}: {
	cloudId: string;
	product: string;
}): Promise<string | undefined> => {
	const cacheKey = `${cloudId}:${product}`;
	const cachedRequest = rovoChatAgentIdentityRequests.get(cacheKey);
	if (cachedRequest) {
		return getIdentityAccountId({ cacheKey, request: cachedRequest });
	}

	const request = performGqlFetch<RovoChatAgentResponse, RovoChatAgentVariables>({
		gqlQuery: `
			query fetchRovoChatAgentIdentity($cloudId: String!, $product: String) {
				agentStudio_getAgents(
					cloudId: $cloudId
					first: 1
					input: { creatorTypes: [ROVO_CHAT] }
					product: $product
				) @optIn(to: "AgentStudio") {
					edges {
						node {
							identityAccountId
						}
					}
				}
			}
		`,
		variables: { cloudId, product },
	});

	rovoChatAgentIdentityRequests.set(cacheKey, request);
	return getIdentityAccountId({ cacheKey, request });
};
